class GrPinfoGallery extends HTMLElement {
  #timer = null;
  #current = 0;

  connectedCallback() {
    this.#track = this.querySelector('.gr-pinfo-gallery__track');
    this.#slides = this.querySelectorAll('.gr-pinfo-gallery__stage');
    this.#thumbs = this.querySelectorAll('.gr-pinfo-thumb');
    this.#intervalMs = Number(this.dataset.interval) || 3000;

    if (!this.#track || this.#slides.length < 2) {
      this.#setActiveThumb(0);
      return;
    }

    this.#thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = Number(thumb.dataset.slide);
        if (Number.isNaN(index)) return;
        this.#goTo(index);
        this.#restartTimer();
      });
    });

    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);

    this.#goTo(0);
    this.#startTimer();
  }

  disconnectedCallback() {
    this.#stopTimer();
    this.removeEventListener('mouseenter', this.#pause);
    this.removeEventListener('mouseleave', this.#resume);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('focusout', this.#resume);
  }

  #track;
  #slides;
  #thumbs;
  #intervalMs;

  #goTo(index) {
    this.#current = ((index % this.#slides.length) + this.#slides.length) % this.#slides.length;
    this.#track.style.transform = `translateX(-${this.#current * 100}%)`;
    this.#setActiveThumb(this.#current);
  }

  #next = () => {
    this.#goTo(this.#current + 1);
  };

  #setActiveThumb(index) {
    this.#thumbs.forEach((thumb) => {
      const slideIndex = Number(thumb.dataset.slide);
      thumb.classList.toggle('is-active', slideIndex === index);
    });
  }

  #startTimer() {
    this.#stopTimer();
    if (this.#slides.length < 2) return;
    this.#timer = window.setInterval(this.#next, this.#intervalMs);
  }

  #stopTimer() {
    if (this.#timer != null) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  #restartTimer() {
    this.#stopTimer();
    this.#startTimer();
  }

  #pause = () => {
    this.#stopTimer();
  };

  #resume = () => {
    this.#startTimer();
  };
}

class GrPinfoReviews extends HTMLElement {
  #timer = null;
  #current = 0;

  connectedCallback() {
    this.#track = this.querySelector('.gr-pinfo-reviews__track');
    this.#cards = this.querySelectorAll('.gr-pinfo-review-card');
    this.#dots = this.querySelectorAll('.gr-pinfo-review-dots__dot');
    this.#intervalMs = Number(this.dataset.interval) || 4000;

    if (!this.#track || this.#cards.length < 2) {
      this.#setActiveDot(0);
      return;
    }

    this.#dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.#goTo(index);
        this.#restartTimer();
      });
    });

    this.addEventListener('mouseenter', this.#pause);
    this.addEventListener('mouseleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);

    this.#goTo(0);
    this.#startTimer();
  }

  disconnectedCallback() {
    this.#stopTimer();
    this.removeEventListener('mouseenter', this.#pause);
    this.removeEventListener('mouseleave', this.#resume);
    this.removeEventListener('focusin', this.#pause);
    this.removeEventListener('focusout', this.#resume);
  }

  #track;
  #cards;
  #dots;
  #intervalMs;

  #goTo(index) {
    this.#current = ((index % this.#cards.length) + this.#cards.length) % this.#cards.length;
    this.#track.style.transform = `translateX(-${this.#current * 100}%)`;
    this.#setActiveDot(this.#current);
  }

  #next = () => {
    this.#goTo(this.#current + 1);
  };

  #setActiveDot(index) {
    this.#dots.forEach((dot, dotIndex) => {
      const isActive = dotIndex === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  #startTimer() {
    this.#stopTimer();
    if (this.#cards.length < 2) return;
    this.#timer = window.setInterval(this.#next, this.#intervalMs);
  }

  #stopTimer() {
    if (this.#timer != null) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  #restartTimer() {
    this.#stopTimer();
    this.#startTimer();
  }

  #pause = () => {
    this.#stopTimer();
  };

  #resume = () => {
    this.#startTimer();
  };
}

const GR_KS_SKELETON_HTML = `
  <div class="gr-ks-gifts-skeleton" aria-hidden="true">
    <div class="gr-ks-gifts-skeleton__header">
      <div class="gr-ks-gifts-skeleton__bar gr-ks-gifts-skeleton__bar--title"></div>
      <div class="gr-ks-gifts-skeleton__bar gr-ks-gifts-skeleton__bar--price"></div>
    </div>
    <div class="gr-ks-gifts-skeleton__items">
      <div class="gr-ks-gifts-skeleton__item">
        <div class="gr-ks-gifts-skeleton__thumb"></div>
        <div class="gr-ks-gifts-skeleton__label"></div>
      </div>
      <div class="gr-ks-gifts-skeleton__item">
        <div class="gr-ks-gifts-skeleton__thumb"></div>
        <div class="gr-ks-gifts-skeleton__label"></div>
      </div>
      <div class="gr-ks-gifts-skeleton__item">
        <div class="gr-ks-gifts-skeleton__thumb"></div>
        <div class="gr-ks-gifts-skeleton__label"></div>
      </div>
    </div>
  </div>
`;

function grKsStripCountryCode(text) {
  return String(text || '')
    .replace(/\bUS\s*\$/gi, '$')
    .replace(/\bUSD\s*/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function grKsParseMoney(text) {
  const match = String(text || '')
    .replace(/,/g, '')
    .match(/(\d+(?:\.\d+)?)/);
  return match ? Number(match[1]) : 0;
}

function grKsFormatMoney(amount) {
  const rounded = Math.round((amount + Number.EPSILON) * 100) / 100;
  const formatted = rounded.toFixed(2).replace(/\.?0+$/, '');
  return `$${formatted}`;
}

function grKsNormalizePriceText(text) {
  let next = grKsStripCountryCode(text);
  next = next.replace(/(\$)\s*(\d+(?:\.\d+)?)/g, (_, _dollar, numStr) =>
    grKsFormatMoney(Number(numStr))
  );
  return next;
}

function grKsStripPricesIn(el) {
  if (!el) return;
  const priceNodes = el.querySelectorAll(
    '.kaching-subscriptions__card-price, .kaching-subscriptions__original-price, .ks-free-gifts__original-price, .ks-free-gifts__title, .gr-ks-gifts-header__total'
  );
  priceNodes.forEach((node) => {
    const next = grKsNormalizePriceText(node.textContent);
    if (next !== node.textContent) node.textContent = next;
  });
}

function grKsInjectSaveBanner(widget) {
  if (!widget || widget.querySelector('.gr-ks-save-banner')) return;

  const label = widget.querySelector('.kaching-subscriptions__selling-plan-label');
  const match = label?.textContent?.match(/(\d+)\s*%/);
  if (!match) return;

  const cards = widget.querySelector('.kaching-subscriptions__cards-wrapper');
  const subCard = cards?.querySelector(
    '.kaching-subscriptions__card-wrapper:not(.one-time-purchase)'
  );
  if (!cards || !subCard) return;

  const banner = document.createElement('div');
  banner.className = 'gr-ks-save-banner';
  banner.textContent = `Save ${match[1]}% On Your First Order`;
  cards.insertBefore(banner, subCard);
}

function grKsEnsureSkeleton(giftsEl) {
  if (!giftsEl.querySelector('.gr-ks-gifts-skeleton')) {
    giftsEl.insertAdjacentHTML('afterbegin', GR_KS_SKELETON_HTML);
  }
}

function grKsEnsureGiftsPlaceholder(widget) {
  const realGifts = widget.querySelector('.ks-free-gifts:not(.gr-ks-gifts-placeholder)');
  const placeholder = widget.querySelector('.gr-ks-gifts-placeholder');
  const tryOnceSelected = widget.querySelector(
    '.kaching-subscriptions__card-wrapper.one-time-purchase.selected'
  );
  const subSelected = widget.querySelector(
    '.kaching-subscriptions__card-wrapper.selected:not(.one-time-purchase)'
  );

  if (tryOnceSelected || !subSelected) {
    placeholder?.remove();
    if (realGifts) {
      realGifts.hidden = true;
      realGifts.setAttribute('aria-hidden', 'true');
    }
    return null;
  }

  if (realGifts) {
    realGifts.hidden = false;
    realGifts.removeAttribute('aria-hidden');
    placeholder?.remove();
    return realGifts;
  }

  if (placeholder) return placeholder;

  const next = document.createElement('div');
  next.className = 'ks-free-gifts gr-ks-gifts-placeholder is-loading';
  next.innerHTML = GR_KS_SKELETON_HTML;
  const cards = widget.querySelector('.kaching-subscriptions__cards-wrapper');
  if (cards) cards.after(next);
  else widget.append(next);
  return next;
}

function grKsWrapGiftImage(row) {
  const img = row.querySelector('.ks-free-gifts__image');
  if (!img || img.closest('.gr-ks-gift-frame')) return;

  img.removeAttribute('width');
  img.removeAttribute('height');
  img.style.removeProperty('width');
  img.style.removeProperty('height');
  img.style.removeProperty('max-width');
  img.style.removeProperty('max-height');

  const frame = document.createElement('div');
  frame.className = 'gr-ks-gift-frame';
  img.before(frame);
  frame.append(img);
}

function grKsEnhanceFreeGifts(widget) {
  const gifts = grKsEnsureGiftsPlaceholder(widget);
  if (!gifts) return;

  grKsEnsureSkeleton(gifts);

  const rows = [
    ...gifts.querySelectorAll(
      ':scope > .ks-free-gifts__row, :scope > .gr-ks-gifts-row > .ks-free-gifts__row'
    ),
  ];
  if (!rows.length) {
    gifts.classList.add('is-loading');
    return;
  }

  gifts.classList.remove('is-loading');

  let total = 0;
  rows.forEach((row) => {
    grKsWrapGiftImage(row);

    const priceEl = row.querySelector('.ks-free-gifts__original-price');
    total += grKsParseMoney(priceEl?.textContent);

    const titleEl = row.querySelector('.ks-free-gifts__title');
    if (titleEl && !titleEl.dataset.grCleaned) {
      const cleaned = grKsStripCountryCode(titleEl.textContent)
        .replace(/\s*FREE\s*$/i, '')
        .trim();
      const words = cleaned.split(/\s+/).filter(Boolean);
      titleEl.replaceChildren();
      words.forEach((word, index) => {
        if (index > 0) titleEl.append(document.createElement('br'));
        titleEl.append(document.createTextNode(word));
      });
      titleEl.dataset.grCleaned = 'true';
    }

    const content = row.querySelector('.ks-free-gifts__content') || row;
    if (!content.querySelector('.gr-ks-gift-free')) {
      const freeLabel = document.createElement('div');
      freeLabel.className = 'gr-ks-gift-free';
      freeLabel.textContent = 'Free';
      content.append(freeLabel);
    }
  });

  let header = gifts.querySelector('.gr-ks-gifts-header');
  if (!header) {
    header = document.createElement('div');
    header.className = 'gr-ks-gifts-header';
    header.innerHTML = `
      <div class="gr-ks-gifts-header__title">Your Free GR Fuel Starter Kit.</div>
      <div class="gr-ks-gifts-header__total"></div>
    `;
    const skeleton = gifts.querySelector('.gr-ks-gifts-skeleton');
    if (skeleton) skeleton.after(header);
    else gifts.prepend(header);
  }

  const totalEl = header.querySelector('.gr-ks-gifts-header__total');
  if (totalEl) totalEl.textContent = grKsFormatMoney(total);

  let rowWrap = gifts.querySelector('.gr-ks-gifts-row');
  if (!rowWrap) {
    rowWrap = document.createElement('div');
    rowWrap.className = 'gr-ks-gifts-row';
    header.after(rowWrap);
  }
  rows.forEach((row) => {
    if (row.parentElement !== rowWrap) rowWrap.append(row);
  });

  gifts.dataset.grEnhanced = 'true';
}

const GR_KS_TICK_SVG = `<svg class="gr-ks-tick ks-feature-icon" width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2.21213 10.7801C2.21213 10.6587 2.1888 10.5981 2.14213 10.5981L1.82013 10.7521C1.82013 10.6867 1.7828 10.6401 1.70813 10.6121L1.59613 10.5981C1.52147 10.5981 1.42813 10.6307 1.31613 10.6961C1.29747 10.6494 1.27413 10.6027 1.24613 10.5561C1.21813 10.5094 1.1948 10.4674 1.17613 10.4301C1.0548 10.1967 0.933466 9.94006 0.812133 9.66006C0.700133 9.37073 0.5928 9.0954 0.490133 8.83406C0.3968 8.57273 0.322133 8.3674 0.266133 8.21806C0.228799 8.09673 0.186799 7.91473 0.140133 7.67206C0.0934662 7.4294 0.0467995 7.1214 0.000132851 6.74806C0.1028 6.8134 0.182133 6.84606 0.238133 6.84606C0.303466 6.84606 0.364133 6.74806 0.420133 6.55206C0.448133 6.5894 0.499466 6.60806 0.574133 6.60806C0.630133 6.60806 0.672133 6.5894 0.700133 6.55206L0.924133 6.21606L1.17613 6.30006H1.19013C1.2088 6.30006 1.22747 6.29073 1.24613 6.27206C1.2648 6.2534 1.2928 6.23473 1.33013 6.21606C1.4048 6.1694 1.4608 6.14606 1.49813 6.14606L1.54013 6.16006C1.77347 6.27206 1.9228 6.4774 1.98813 6.77606C2.15613 7.4854 2.32413 7.84006 2.49213 7.84006C2.66013 7.84006 2.85613 7.66273 3.08013 7.30806C3.19213 7.13073 3.30413 6.9254 3.41613 6.69206C3.53747 6.45873 3.6588 6.1974 3.78013 5.90806C3.7988 6.02006 3.81747 6.07606 3.83613 6.07606C3.8828 6.07606 3.96213 5.9594 4.07413 5.72606C4.19547 5.49273 4.3868 5.17073 4.64813 4.76006C4.79747 4.50806 4.98413 4.2234 5.20813 3.90606C5.44147 3.58873 5.6888 3.26206 5.95013 2.92606C6.21147 2.59006 6.46347 2.27273 6.70613 1.97406C6.95813 1.6754 7.18213 1.41873 7.37813 1.20406C7.57413 0.989395 7.7188 0.849395 7.81213 0.784062C8.1668 0.541395 8.4468 0.308062 8.65213 0.0840621C8.6428 0.149395 8.6288 0.210062 8.61013 0.266062C8.6008 0.312729 8.59613 0.345395 8.59613 0.364062C8.59613 0.401395 8.6148 0.420062 8.65213 0.420062L9.04413 0.224062V0.280062C9.04413 0.354729 9.0628 0.392062 9.10013 0.392062C9.12813 0.392062 9.18413 0.350062 9.26813 0.266062C9.35213 0.182062 9.3988 0.121396 9.40813 0.0840621L9.38013 0.280062L9.85613 6.23465e-05L9.74413 0.252062C9.89347 0.149395 10.0008 0.0980619 10.0661 0.0980619C10.1035 0.0980619 10.1315 0.121395 10.1501 0.168062C10.1688 0.205395 10.1781 0.242729 10.1781 0.280062C10.1781 0.336062 10.1548 0.401395 10.1081 0.476062C10.0615 0.550729 10.0008 0.639396 9.92613 0.742062C9.87013 0.816729 9.7768 0.928729 9.64613 1.07806C9.5248 1.21806 9.33813 1.42806 9.08613 1.70806C8.83413 1.97873 8.49813 2.35673 8.07813 2.84206C7.96613 2.9634 7.79347 3.17806 7.56013 3.48606C7.3268 3.78473 7.0608 4.13473 6.76213 4.53606C6.4728 4.92806 6.18347 5.32473 5.89413 5.72606C5.6048 6.1274 5.34813 6.4914 5.12413 6.81806C4.90013 7.1354 4.74147 7.3734 4.64813 7.53206L3.78013 9.00206C3.59347 9.3194 3.43947 9.58073 3.31813 9.78606C3.1968 9.98206 3.10347 10.1174 3.03813 10.1921C2.89813 10.3601 2.74413 10.5094 2.57613 10.6401L2.45013 10.5701L2.33813 10.6401L2.21213 10.7801Z" fill="#F16263"/></svg>`;

function grKsReplaceFeatureTicks(widget) {
  widget.querySelectorAll('.ks-feature-item .ks-feature-icon, .ks-feature-item svg.ks-feature-icon').forEach((icon) => {
    if (icon.classList?.contains('gr-ks-tick') || icon.dataset?.grTick === 'true') return;
    const wrap = document.createElement('span');
    wrap.className = 'gr-ks-tick-wrap';
    wrap.innerHTML = GR_KS_TICK_SVG;
    const tick = wrap.firstElementChild;
    if (tick) tick.dataset.grTick = 'true';
    icon.replaceWith(tick || wrap);
  });
}

function grKsProductHasVariants() {
  const details = document.querySelector('.gr-pinfo-details[data-has-variants="true"]');
  if (details) return true;

  const purchase = document.querySelector('.gr-pinfo-details__purchase');
  return Boolean(
    purchase?.querySelector('variant-picker.gr-variant-picker, variant-picker:not([data-shopify-visual-preview])')
  );
}

function grKsInjectPerDay(widget) {
  if (grKsProductHasVariants()) {
    widget.querySelectorAll('.gr-ks-per-day').forEach((el) => el.remove());
    return;
  }

  widget.querySelectorAll('.kaching-subscriptions__card-wrapper').forEach((card) => {
    const priceEl = card.querySelector('.kaching-subscriptions__card-price');
    const amount = grKsParseMoney(priceEl?.textContent);
    if (!amount) return;

    const title = card.querySelector('.kaching-subscriptions__card-title');
    if (!title) return;

    const host =
      title.closest('.kaching-subscriptions__flex-col-center') ||
      title.parentElement;
    if (!host) return;

    let perDayEl = host.querySelector('.gr-ks-per-day');
    if (!perDayEl) {
      perDayEl = document.createElement('div');
      perDayEl.className = 'gr-ks-per-day';
      title.after(perDayEl);
    }
    perDayEl.textContent = `${grKsFormatMoney(amount / 30)} Per Day`;
  });
}

function grKsSyncStickyPrice(widget) {
  const selectedCard = widget.querySelector(
    '.kaching-subscriptions__card-wrapper.selected'
  );
  const planPriceEl = selectedCard?.querySelector('.kaching-subscriptions__card-price');
  if (!planPriceEl) return;

  const priceText = grKsNormalizePriceText(planPriceEl.textContent);
  if (!priceText) return;

  const sticky = document.querySelector('sticky-add-to-cart');
  if (!sticky) return;

  const stickyPrice = sticky.querySelector('.sticky-add-to-cart__price .price');
  if (stickyPrice) {
    stickyPrice.textContent = priceText;
    return;
  }

  const priceContainer = sticky.querySelector(
    '.sticky-add-to-cart__price [ref="priceContainer"], .sticky-add-to-cart__price'
  );
  if (!priceContainer) return;

  let priceSpan = priceContainer.querySelector('.price');
  if (!priceSpan) {
    priceSpan = document.createElement('span');
    priceSpan.className = 'price';
    priceContainer.replaceChildren(priceSpan);
  }
  priceSpan.textContent = priceText;
}

function grKsEnhanceWidget(host) {
  if (!host?.querySelector) return;

  const target = host.matches?.('.kaching-subscriptions__widget')
    ? host
    : host.querySelector('.kaching-subscriptions__widget') || host;

  // Remove any leftover radio ticks from earlier iterations
  target.querySelectorAll('.gr-ks-radio-tick').forEach((el) => el.remove());

  grKsInjectSaveBanner(target);
  grKsStripPricesIn(target);
  grKsReplaceFeatureTicks(target);
  grKsInjectPerDay(target);
  grKsEnhanceFreeGifts(target);
  grKsSyncStickyPrice(target);
}

function grKsInit() {
  const purchase = document.querySelector('.gr-pinfo-details__purchase');
  if (!purchase) return;

  let frame = 0;
  const run = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      purchase
        .querySelectorAll('kaching-subscriptions, .kaching-subscriptions__widget')
        .forEach((el) => {
          grKsEnhanceWidget(el);
        });
    });
  };

  run();

  const observer = new MutationObserver(run);
  observer.observe(purchase, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });
}

class GrSupplementFactsSync extends HTMLElement {
  /** @type {(event: Event) => void} */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (this.dataset.hasVariants !== 'true') return;

    const productSelectEvent = /** @type {{ promise?: Promise<{ detail?: { productId?: string, resource?: { id?: string | number } } }> } } */ (
      event
    );

    productSelectEvent.promise
      ?.then(({ detail }) => {
        const productId = detail?.productId;
        if (productId && productId !== this.dataset.productId) return;

        const variantId = detail?.resource?.id;
        if (!variantId) return;

        this.#applyVariant(String(variantId));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[gr-supplement-facts-sync] Event promise rejected:', error);
        }
      });
  };

  connectedCallback() {
    if (this.dataset.hasVariants !== 'true') return;

    this.#variantData = this.#parseVariantData();
    document.addEventListener('shopify:product:select', this.#handleProductSelect);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:product:select', this.#handleProductSelect);
  }

  /** @type {{ variants?: Record<string, { flavor?: string, html?: string }> } | null} */
  #variantData = null;

  #parseVariantData() {
    const script = this.querySelector('[data-gr-sf-variant-data]');
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  /** @param {string} variantId */
  #applyVariant(variantId) {
    const entry = this.#variantData?.variants?.[variantId];
    if (!entry) return;

    const labelEl = this.querySelector('[data-gr-sf-label]');
    if (labelEl instanceof HTMLElement) {
      if (entry.html) {
        labelEl.innerHTML = entry.html;
        labelEl.hidden = false;
      } else {
        labelEl.innerHTML = '';
        labelEl.hidden = true;
      }
    }

    const flavorValueEl = this.querySelector('[data-gr-sf-flavor-value]');
    if (flavorValueEl && entry.flavor != null) {
      flavorValueEl.textContent = entry.flavor;
    }
  }
}

if (!customElements.get('gr-pinfo-gallery')) {
  customElements.define('gr-pinfo-gallery', GrPinfoGallery);
}

if (!customElements.get('gr-pinfo-reviews')) {
  customElements.define('gr-pinfo-reviews', GrPinfoReviews);
}

if (!customElements.get('gr-supplement-facts-sync')) {
  customElements.define('gr-supplement-facts-sync', GrSupplementFactsSync);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', grKsInit, { once: true });
} else {
  grKsInit();
}
